import { useState, useEffect, useRef } from 'react';
import { uploadImageToSupabase, formatImageUploadError } from './uploadImage';
import './ShopPhotosEditor.css';

export function normalizePhotoUrls(val) {
  if (!val) return [];
  if (Array.isArray(val)) return val.map(String).map((s) => s.trim()).filter(Boolean);
  if (typeof val === 'string' && val.trim()) return [val.trim()];
  return [];
}

export default function ShopPhotosEditor({ value, onChange, disabled, onError }) {
  const [urls, setUrls] = useState(() => normalizePhotoUrls(value));
  const [showJson, setShowJson] = useState(false);
  const [jsonDraft, setJsonDraft] = useState('');
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);

  useEffect(() => {
    setUrls(normalizePhotoUrls(value));
  }, [value]);

  const commit = (next) => {
    setUrls(next);
    onChange(next);
  };

  const remove = (idx) => {
    commit(urls.filter((_, i) => i !== idx));
  };

  const appendUrl = (url) => {
    const trimmed = String(url || '').trim();
    if (!trimmed || urls.includes(trimmed)) return;
    commit([...urls, trimmed]);
  };

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    setUploading(true);
    try {
      const publicUrl = await uploadImageToSupabase(file, { folder: 'shops' });
      appendUrl(publicUrl);
    } catch (err) {
      onError?.(formatImageUploadError(err));
    } finally {
      setUploading(false);
    }
  };

  const handlePaste = async () => {
    try {
      if (!navigator?.clipboard?.readText) {
        throw new Error('Tarayıcı panoya erişim izni vermedi.');
      }
      const text = (await navigator.clipboard.readText()).trim();
      if (!text) {
        onError?.('Panoda link bulunamadı.');
        return;
      }
      appendUrl(text);
    } catch (err) {
      onError?.(err?.message || 'Panodan link yapıştırılamadı.');
    }
  };

  const applyJson = () => {
    try {
      const parsed = JSON.parse(jsonDraft || '[]');
      if (!Array.isArray(parsed)) throw new Error('JSON bir dizi olmalı');
      commit(normalizePhotoUrls(parsed));
      setShowJson(false);
    } catch (err) {
      onError?.(err?.message || 'Geçersiz JSON');
    }
  };

  return (
    <div className="shop-photos-editor">
      {urls.length ? (
        <div className="shop-photos-thumbs">
          {urls.map((url, idx) => (
            <div key={`${url}-${idx}`} className="shop-photos-thumb">
              <img src={url} alt="" loading="lazy" />
              <button
                type="button"
                className="shop-photos-remove"
                onClick={() => remove(idx)}
                disabled={disabled}
                title="Kaldır"
              >
                ×
              </button>
            </div>
          ))}
        </div>
      ) : (
        <p className="shop-photos-empty">Henüz görsel yok</p>
      )}
      <div className="shop-photos-actions">
        <button
          type="button"
          className="shift-submit"
          style={{ padding: '0.24rem 0.45rem', fontSize: '0.68rem' }}
          disabled={disabled || uploading}
          onClick={() => fileRef.current?.click()}
        >
          {uploading ? '…' : 'Yükle'}
        </button>
        <button
          type="button"
          className="shift-submit"
          style={{ padding: '0.24rem 0.45rem', fontSize: '0.68rem' }}
          disabled={disabled}
          onClick={handlePaste}
        >
          Link yapıştır
        </button>
        <button
          type="button"
          className="shift-delete"
          style={{ padding: '0.24rem 0.45rem', fontSize: '0.68rem' }}
          disabled={disabled}
          onClick={() => {
            setJsonDraft(JSON.stringify(urls, null, 2));
            setShowJson((v) => !v);
          }}
        >
          JSON
        </button>
        <input
          ref={fileRef}
          type="file"
          accept="image/*"
          hidden
          onChange={handleUpload}
        />
      </div>
      {showJson ? (
        <div className="shop-photos-json">
          <textarea
            className="shift-input"
            value={jsonDraft}
            onChange={(e) => setJsonDraft(e.target.value)}
            rows={3}
            style={{ width: '100%', fontFamily: 'monospace', fontSize: '0.68rem' }}
          />
          <button
            type="button"
            className="shift-submit"
            style={{ marginTop: 4, padding: '0.2rem 0.4rem', fontSize: '0.65rem' }}
            onClick={applyJson}
          >
            Uygula
          </button>
        </div>
      ) : null}
    </div>
  );
}
